import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  useMediaQuery,
  useTheme,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Home as HomeIcon,
  LiveTv as LiveTvIcon,
  Star as StarIcon,
  SportsFootball as SportsFootballIcon,
  SportsMma as SportsMmaIcon,
  SportsBasketball as SportsBasketballIcon,
  SportsBaseball as SportsBaseballIcon,
  SportsHockey as SportsHockeyIcon,
  SportsSoccer as SportsSoccerIcon,
  SportsTennis as SportsTennisIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  LocalOffer as LocalOfferIcon,
  SportsScore as SportsIcon,
  SmartToy as SmartToyIcon,
  MenuBook as MenuBookIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Calculate as CalculateIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useRole } from '../contexts/RoleContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';

const SIDEBAR_WIDTH = 240;

const LeftSidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin, isHandicapper, isHandicapperOrAdmin } = useRole();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [gamesExpanded, setGamesExpanded] = useState(true);
  const [expandedSport, setExpandedSport] = useState(null);
  const [affiliateGranted, setAffiliateGranted] = useState(false);

  // The Affiliate entry is invite-only: it appears only for users whose
  // own doc carries affiliateAccess.status === 'granted' (stamped by an
  // admin through the manage-affiliates function).
  useEffect(() => {
    let isMounted = true;

    const loadAffiliateAccess = async () => {
      if (!currentUser) {
        if (isMounted) setAffiliateGranted(false);
        return;
      }
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const access = userSnap.exists() ? userSnap.data().affiliateAccess : null;
        if (isMounted) {
          setAffiliateGranted(!!access && access.status === 'granted');
        }
      } catch (error) {
        console.error('Error loading affiliate access:', error);
        if (isMounted) setAffiliateGranted(false);
      }
    };

    loadAffiliateAccess();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const getQuickLinks = () => {
    const baseLinks = [
      { text: 'Home', icon: <HomeIcon />, path: '/' },
      { text: 'All Odds', icon: <AssessmentIcon />, path: '/odds' },
      { text: 'Player Props', icon: <PersonIcon />, path: '/player-props' },
      { text: 'Parlay Builder', icon: <CalculateIcon />, path: '/parlay-builder' },
      { text: 'Bet Tracker', icon: <ReceiptLongIcon />, path: '/bet-tracker' },
      { text: 'All Sports', icon: <SportsIcon />, path: '/allsports' },
      { text: 'Promotions', icon: <LocalOfferIcon />, path: '/promos' },
      { text: 'Bettor Picks', icon: <StarIcon />, path: '/handicappers' },
      { text: 'Resources', icon: <MenuBookIcon />, path: '/resources' },
      // { text: 'Live', icon: <LiveTvIcon />, path: '/live-games' }, // Commented out
    ];

    // Add role-specific links
    const roleLinks = [];
    if (isHandicapperOrAdmin) {
      roleLinks.push({ text: 'My Picks', icon: <TrendingUpIcon />, path: '/handicapper-dashboard' });
    }
    if (affiliateGranted) {
      roleLinks.push({ text: 'Affiliate', icon: <AttachMoneyIcon />, path: '/affiliate' });
    }
    if (isAdmin) {
      roleLinks.push({ text: 'Admin Dashboard', icon: <AdminPanelSettingsIcon />, path: '/admin' });
      roleLinks.push({ text: 'BettorAI', icon: <SmartToyIcon />, path: '/bettor-ai' });
    }

    return [...baseLinks, ...roleLinks];
  };

  const quickLinks = getQuickLinks();

  // The All Sports menu for the drawer: every sport, with its sections,
  // mirroring the desktop hover dropdown.
  const SPORT_SECTIONS = ['overview', 'scores', 'schedule', 'odds', 'futures', 'props', 'news', 'teams'];
  const sectionLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const sportCategories = [
    { text: 'NFL', slug: 'nfl', icon: <SportsFootballIcon /> },
    { text: 'NBA', slug: 'nba', icon: <SportsBasketballIcon /> },
    { text: 'MLB', slug: 'mlb', icon: <SportsBaseballIcon /> },
    { text: 'NHL', slug: 'nhl', icon: <SportsHockeyIcon /> },
    { text: 'Soccer', slug: 'soccer', icon: <SportsSoccerIcon /> },
    { text: 'NCAAF', slug: 'ncaaf', icon: <SportsFootballIcon /> },
    { text: 'NCAAB', slug: 'ncaab', icon: <SportsBasketballIcon /> },
    { text: 'WNBA', slug: 'wnba', icon: <SportsBasketballIcon /> },
    { text: 'UFC', slug: 'ufc', icon: <SportsMmaIcon /> },
    { text: 'Boxing', slug: 'boxing', icon: <SportsMmaIcon /> },
    { text: 'Tennis', slug: 'tennis', icon: <SportsTennisIcon /> },
    { text: 'Golf', slug: 'golf', icon: <SportsIcon /> },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        bgcolor: 'primary.main',
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        overflowY: 'auto',
      }}
    >
      {/* Mobile Close Button */}
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Quick Links */}
      <List sx={{ px: 1, pt: isMobile ? 0 : 2 }}>
        {quickLinks.map((link) => (
          <ListItem key={link.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(link.path)}
              selected={isActive(link.path)}
              sx={{
                borderRadius: 1,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-selected': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                {link.badge ? (
                  <Badge badgeContent="●" color="error" variant="dot">
                    {link.icon}
                  </Badge>
                ) : (
                  link.icon
                )}
              </ListItemIcon>
              <ListItemText
                primary={link.text}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: isActive(link.path) ? 600 : 400,
                  color: 'white',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Games Section */}
      <Box sx={{ px: 1, mt: 2 }}>
        <Accordion
          expanded={gamesExpanded}
          onChange={() => setGamesExpanded(!gamesExpanded)}
          sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{
              minHeight: 40,
              px: 2,
              '& .MuiAccordionSummary-content': {
                my: 1,
              },
            }}
          >
            <Box sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'white' }}>
              Sports
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {sportCategories.map((sport) => (
              <Accordion
                key={sport.slug}
                expanded={expandedSport === sport.slug}
                onChange={() => setExpandedSport(expandedSport === sport.slug ? null : sport.slug)}
                sx={{
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  sx={{
                    minHeight: 40,
                    px: 1,
                    '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'white' }}>{sport.icon}</ListItemIcon>
                  <Box sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'white' }}>{sport.text}</Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List sx={{ py: 0 }}>
                    {SPORT_SECTIONS.map((section) => (
                      <ListItem key={section} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigation(`/sport/${sport.slug}/${section}`)}
                          selected={isActive(`/sport/${sport.slug}/${section}`)}
                          sx={{
                            borderRadius: 1,
                            pl: 6,
                            py: 0.5,
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                            '&.Mui-selected': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                          }}
                        >
                          <ListItemText
                            primary={sectionLabel(section)}
                            primaryTypographyProps={{ fontSize: '0.85rem', color: 'white' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );

  // Desktop: Permanent drawer
  // Mobile: Temporary drawer with backdrop
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: SIDEBAR_WIDTH,
          height: '100vh',
          zIndex: 1100,
        }}
      >
        {sidebarContent}
      </Box>
    </Box>
  );
};

export default LeftSidebar;
